import { AdminClientPayload, BrandFormValues, ClientBrand, GlobalClientPayload, JobDescriptionBoard, NewClientBrandData, UpsertCobrandPayload } from "../types";

const CHECKSUM_PLACEHOLDER = "asd";
const ADMIN_CLIENT_CHECKSUM_PLACEHOLDER = "a";
export const LARGE_OPTIONS_THRESHOLD = 1000;

interface JobDescriptionBoardOptionFilesMetadata {
  customFieldId?: string;
  customFieldName?: string;
  fieldIndex: number;
  fileName: string;
  optionsCount: number;
}

export function appendFileData(formData: FormData, file: File, checksum: string, subName = "") {
  const nextFormData = formData;
  if (file) {
    nextFormData.set(`fileName${subName}`, file?.name);
    nextFormData.append(`file${subName}`, file);
    nextFormData.set(`checksum${subName}`, checksum);
  }
  return nextFormData;
}

export function buildCobrandFormData(cobrandPayload: UpsertCobrandPayload) {
  if (!cobrandPayload) return null;

  let cobrandFormData = new FormData();
  const { emailHeaderImage, ...cobrandData } = cobrandPayload;

  Object.entries(cobrandData).forEach(([key, value]) => {
    if (value == null) return;

    if (key === "tenants" && Array.isArray(value)) {
      cobrandFormData.set(key, JSON.stringify(value));
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => cobrandFormData.append(key, String(item)));
    } else if (typeof value === "object") {
      cobrandFormData.set(key, JSON.stringify(value));
    } else {
      cobrandFormData.set(key, String(value));
    }
  });

  if (emailHeaderImage instanceof File) {
    cobrandFormData = appendFileData(cobrandFormData, emailHeaderImage, CHECKSUM_PLACEHOLDER, "emailHeaderImage");
    cobrandFormData.set("type", emailHeaderImage.type);
  }

  return cobrandFormData;
}

export function buildBrandFormData(brandPayload: BrandFormValues): FormData | null {
  if (!brandPayload) return null;

  const { emailHeaderImage, ...brandData } = brandPayload;
  let brandFormData = new FormData();

  Object.entries(brandData).forEach(([key, value]) => {
    if (value == null) return;

    if (key === "cammioBrandingId" && typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value as Record<string, string>).forEach(([envKey, envValue]) => {
        if (envValue) brandFormData.set(`cammioBrandingId.${envKey}`, JSON.stringify(envValue));
      });
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => brandFormData.append(key, String(item)));
    } else if (typeof value === "object") {
      brandFormData.set(key, JSON.stringify(value));
    } else {
      brandFormData.set(key, String(value));
    }
  });

  if (Array.isArray(emailHeaderImage)) {
    brandFormData = appendFileData(brandFormData, emailHeaderImage[1], CHECKSUM_PLACEHOLDER, "emailHeaderImage");
    brandFormData.set("type", emailHeaderImage[1].type);
  } else if (emailHeaderImage instanceof File) {
    brandFormData = appendFileData(brandFormData, emailHeaderImage, CHECKSUM_PLACEHOLDER, "emailHeaderImage");
    brandFormData.set("type", emailHeaderImage.type);
  } else if (typeof emailHeaderImage === "string" && emailHeaderImage) {
    brandFormData.set("emailHeaderImage", emailHeaderImage);
  }

  return brandFormData;
}

export function buildGlobalClientFormData(globalClientPayload: GlobalClientPayload): FormData | null {
  if (!globalClientPayload) return null;

  let globalClientFormData = new FormData();
  globalClientFormData.set("name", globalClientPayload.name);

  if (globalClientPayload.file) {
    globalClientFormData = appendFileData(globalClientFormData, globalClientPayload.file, CHECKSUM_PLACEHOLDER);
  }

  return globalClientFormData;
}

export function buildAdminClientFormData(adminClientPayload: AdminClientPayload): FormData | null {
  if (!adminClientPayload) return null;

  let adminClientFormData = new FormData();
  const logoRemoved = adminClientPayload.logoRemoved ?? false;
  adminClientFormData.set("name", adminClientPayload.name);
  adminClientFormData.set("showFindJobsUrl", adminClientPayload.showFindJobsUrl ? "true" : "false");
  adminClientFormData.set("logoRemoved", logoRemoved ? "true" : "false");

  if (adminClientPayload.globalId) adminClientFormData.set("globalId", adminClientPayload.globalId);
  adminClientPayload.branches?.forEach((branchId) => adminClientFormData.append("branches[]", branchId));

  if (adminClientPayload.file) {
    adminClientFormData = appendFileData(adminClientFormData, adminClientPayload.file, ADMIN_CLIENT_CHECKSUM_PLACEHOLDER);
  }

  return adminClientFormData;
}

export const buildClientBrandFormData = (clientBrandPayload: NewClientBrandData | ClientBrand, primaryLogoFile?: File, secondaryLogoFile?: File): FormData | null => {
  if (!clientBrandPayload) return null;

  let clientBrandFormData = new FormData();

  if (primaryLogoFile) {
    clientBrandFormData = appendFileData(clientBrandFormData, primaryLogoFile, CHECKSUM_PLACEHOLDER, "PrimaryLogo");
    clientBrandFormData.set("contentTypePrimaryLogo", primaryLogoFile.type);
  }

  if (secondaryLogoFile) {
    clientBrandFormData = appendFileData(clientBrandFormData, secondaryLogoFile, CHECKSUM_PLACEHOLDER, "SecondaryLogo");
    clientBrandFormData.set("contentTypeSecondaryLogo", secondaryLogoFile.type);
  }

  clientBrandFormData.set("clientBrand", JSON.stringify(clientBrandPayload));

  if (clientBrandPayload?.emailHeaderImage instanceof File) {
    clientBrandFormData = appendFileData(clientBrandFormData, clientBrandPayload.emailHeaderImage, CHECKSUM_PLACEHOLDER, "emailHeaderImage");
    clientBrandFormData.set("contentTypeEmailHeaderImage", clientBrandPayload.emailHeaderImage.type);
  }

  return clientBrandFormData;
};

function escapeCsvValue(value: unknown): string {
  const stringValue = value === null || value === undefined ? "" : String(value);
  const escapedValue = stringValue.replace(/"/g, '""');

  if (/[",\r\n]/.test(escapedValue)) {
    return `"${escapedValue}"`;
  }

  return escapedValue;
}

function buildOptionsCsv(options: any[] = [], includeDependentValue = false): string {
  const headers = includeDependentValue ? ["key", "value", "dependentValue"] : ["key", "value"];
  const rows = options.map((option) => {
    const values = [escapeCsvValue(option?.value), escapeCsvValue(option?.label)];
    if (includeDependentValue) values.push(escapeCsvValue(option?.dependentValue));
    return values.join(",");
  });
  return [headers.join(","), ...rows].join("\r\n");
}

function buildOptionFileName(field: any, fieldIndex: number): string {
  const fieldIdentifier = field?.id || field?.name || `field-${fieldIndex}`;
  return `job-board-custom-field-options-${fieldIdentifier}.csv`.replace(/[^a-zA-Z0-9._-]/g, "_");
}

function buildCsvFile(content: string): Blob {
  return new Blob([content], { type: "text/csv;charset=utf-8" });
}

export function buildJobDescriptionBoardFormData(jobDescriptionBoard: JobDescriptionBoard): FormData | null {
  const customFields = jobDescriptionBoard?.customFields;

  if (!Array.isArray(customFields)) return null;

  const optionFilesMetadata: JobDescriptionBoardOptionFilesMetadata[] = [];
  const optionFiles: { file: Blob; fileName: string }[] = [];
  const nextCustomFields = customFields.map((field, fieldIndex) => {
    if (!Array.isArray(field?.options) || field.options.length <= LARGE_OPTIONS_THRESHOLD) return field;

    const fileName = buildOptionFileName(field, fieldIndex);
    optionFilesMetadata.push({
      customFieldId: field?.id,
      customFieldName: field?.name,
      fieldIndex,
      fileName,
      optionsCount: field.options.length,
    });
    const includeDependentValue = field?.type === "dependentSearchSelect" || Boolean(field?.parentField);
    optionFiles.push({
      file: buildCsvFile(buildOptionsCsv(field.options, includeDependentValue)),
      fileName,
    });

    const fieldWithoutOptions = { ...field };
    delete fieldWithoutOptions.options;
    return fieldWithoutOptions;
  });

  if (optionFiles.length === 0) return null;

  const formData = new FormData();
  formData.set("board", JSON.stringify({ ...jobDescriptionBoard, customFields: nextCustomFields }));
  optionFiles.forEach(({ file, fileName }) => formData.append("optionFiles", file, fileName));
  formData.set("optionFilesMetadata", JSON.stringify(optionFilesMetadata));

  return formData;
}
